


import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button,
  DialogContentText 
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import MenuIcon from '@mui/icons-material/Menu';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { AUTH_URLS, CATALOG_URLS } from '../config/urls';
import CatalogImagery from './CatalogImagery';
import DeleteIcon from '@mui/icons-material/Delete';


const OrderPage = () => {
  const history = useHistory();
  const [tabValue, setTabValue] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    orderId: null,
    status: '',
    message: ''
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOrderId, setMenuOrderId] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    orderId: null
  });
  


  const tabCounts = {
    tasking: orders.filter(order => order.order_type === 'tasking' && order.status !== 'draft').length,
    archival: orders.filter(order => order.order_type === 'catalog' && order.status !== 'draft').length,
    drafts: orders.filter(order => order.status === 'draft').length,
    all: allOrders.length // <-- this will be shown only if Super Admin
  
  };
  
  

  // useEffect(() => {
  //   fetchOrders();
  // }, []);
  
  // Decode JWT token function
  const decodeToken = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
      return null;
    }
  };

  // Effect to fetch users and set current user
// Move fetchOrders inside the useEffect that depends on users
useEffect(() => {
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const decoded = decodeToken(token);
      const response = await axios.get(AUTH_URLS.me, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCurrentUser(response.data);
      fetchOrders(response.data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  fetchUsers();
}, [tabValue]); // 👈 refetch when tab changes


// Modify fetchOrders to accept the role as a parameter
const fetchOrders = async (userData) => {
  try {
    const token = localStorage.getItem('token');

    if (userData?.role === 'Super Admin') {
      // Fetch both user-specific and all orders
      const [userOrdersResponse, adminOrdersResponse] = await Promise.all([
        axios.get(CATALOG_URLS.getAllOrders, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(CATALOG_URLS.getAllOrdersAdmin, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setOrders(userOrdersResponse.data);
      setAllOrders(adminOrdersResponse.data);
    } else {
      // Normal users only fetch their own orders
      const response = await axios.get(CATALOG_URLS.getAllOrders, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    }

    setLoading(false);
  } catch (error) {
    console.error('Error fetching orders:', error);
    setError('Failed to fetch orders');
    setLoading(false);
  }
};




  // Navigate to data delivery page
  const handleViewOrder = (orderId) => {
    history.push(`/delivery/${orderId}`);
  };

  // Open menu for status change and other actions
  const handleMenuOpen = (event, orderId) => {
    setAnchorEl(event.currentTarget);
    setMenuOrderId(orderId);
  };

  // Close the menu
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuOrderId(null);
  };

  // Check if current user is a super admin
  const isSuperAdmin = () => {
    return currentUser && currentUser.role === 'Super Admin';
  };

  // Filter orders based on search term
  const getFilteredOrders = () => {
    const searchTermLower = searchTerm.toLowerCase();
    const relevantOrders = (tabValue === 3 && isSuperAdmin()) ? allOrders : orders;
  
    return relevantOrders.filter(order => {
      const matchesId = order.id.toLowerCase().includes(searchTermLower);
      const matchesProjectName = order.project_name 
        ? order.project_name.toLowerCase().includes(searchTermLower)
        : false;
      const matchesConstellation = order.selected_images?.[0]?.satellite 
        ? order.selected_images[0].satellite.toLowerCase().includes(searchTermLower)
        : false;
  
      const matchesTab = 
        (tabValue === 0 && order.order_type === 'tasking' && order.status !== 'draft') ||
        (tabValue === 1 && order.order_type === 'catalog' && order.status !== 'draft') ||
        (tabValue === 2 && order.status === 'draft') ||
        (tabValue === 3); // All orders
  
      return (matchesId || matchesProjectName || matchesConstellation) && matchesTab;
    });
  };
  

  // Handle status update button click
  const handleStatusUpdate = (order, newStatus) => {
    const messages = {
      requested: 'Are you sure you want to submit this order?',
      Accepted: 'Are you sure you want to accept this order?',
      rejected: 'Are you sure you want to reject this order?',
      Delivered: 'Are you sure you want to mark this order as delivered?'
    };

  
    setConfirmDialog({
      open: true,
      orderId: order.id,
      status: newStatus,
      message: messages[newStatus]
    });
    
    handleMenuClose();
  };
  
  const handleConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        CATALOG_URLS.updateOrderStatus(confirmDialog.orderId),
        { status: confirmDialog.status },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
  
      // Refresh orders after status update
      fetchOrders();
      setConfirmDialog({ open: false, orderId: null, status: '', message: '' });
    } catch (error) {
      console.error('Error updating order status:', error);
      setError('Failed to update order status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const formatter = new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  
    // Format the date and then capitalize the month
    const parts = formatter.formatToParts(date);
    const day = parts.find(part => part.type === 'day').value;
    const month = parts.find(part => part.type === 'month').value;
    const year = parts.find(part => part.type === 'year').value;
  
    return `${day} ${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
  };

  // Move these OUTSIDE the getFilteredOrders function, but BEFORE using filteredOrders
  if (loading) {
    return <Box sx={{ p: 3 }}>Loading orders...</Box>;
  }

  if (error) {
    return <Box sx={{ p: 3, color: 'error.main' }}>{error}</Box>;
  }

  const filteredOrders = getFilteredOrders();

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        borderBottom: 1, 
        borderColor: 'divider', 
        mb: 2 
      }}>
        {/* Tabs Section */}
        <Tabs 
  value={tabValue} 
  onChange={(event, newValue) => setTabValue(newValue)}
  sx={{
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 'normal',
      color: '#666',
      '&.Mui-selected': {
        color: '#000'
      }
    }
  }}
>
  <Tab label={`Tasking (${tabCounts.tasking})`} value={0} />
  <Tab label={`Catalog (${tabCounts.archival})`} value={1} />
  <Tab label={`Drafts (${tabCounts.drafts})`} value={2} />
  {isSuperAdmin() && (
    <Tab label={`All Orders (${tabCounts.all})`} value={3} />
  )}
</Tabs>


        {/* Search Section - Positioned on the right */}
        <Box sx={{ width: '250px' }}>
          <TextField
            fullWidth
            placeholder="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              bgcolor: '#f5f5f5',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'transparent'
                },
                '&:hover fieldset': {
                  borderColor: 'transparent'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent'
                }
              }
            }}
          />
        </Box>
      </Box>

      {/* Current user role display - for debugging purposes, can be removed in production */}
      {/* {currentUser && (
        <Box sx={{ mb: 2, px: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Logged in as: {currentUser.email} | Role: {currentUser.role || 'User'}
          </Typography>
        </Box>
      )} */}

      {/* Table Section */}
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table sx={{ minWidth: 650 }} aria-label="orders table">
          {/* Table Header */}
          <TableHead>
            <TableRow>
            {isSuperAdmin() && tabValue === 3 && (
  <TableCell sx={{ color: '#666', fontWeight: 500 }}>USER</TableCell>
)}


              <TableCell sx={{ color: '#666', fontWeight: 500 }}>ORDER NAME</TableCell>
              <TableCell sx={{ color: '#666', fontWeight: 500 }}>ORDER ID</TableCell>
              <TableCell sx={{ color: '#666', fontWeight: 500 }}>CONSTELLATION</TableCell>
              <TableCell sx={{ color: '#666', fontWeight: 500 }}>REQUESTED ON</TableCell>
              <TableCell sx={{ color: '#666', fontWeight: 500 }}>DELIVERY DATE</TableCell>
              <TableCell sx={{ color: '#666', fontWeight: 500 }}>STATUS</TableCell>
              <TableCell sx={{ color: '#666', fontWeight: 500 }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {filteredOrders.map((order) => (
              <TableRow
                key={order.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                {isSuperAdmin() && tabValue === 3 && (
  <TableCell>{order?.user_name || order?.user?.name || 'N/A'}</TableCell>
)}

                <TableCell>{order.project_name || 'Untitled Order'}</TableCell>
                <TableCell>{order.id}</TableCell>
                <TableCell>{order.selected_images?.[0]?.satellite || 'N/A'}</TableCell>
                <TableCell>
                  {formatDate(order.created_at)}
                </TableCell>
                <TableCell>
                  {formatDate(order?.delivery_date || order.created_at)}
                </TableCell>
                <TableCell>
                  {isSuperAdmin() ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box 
                        onClick={(event) => {
                          setAnchorEl(event.currentTarget);
                          setMenuOrderId(order.id);
                        }}
                        sx={{ 
                          bgcolor: 
                            order.status === 'requested' ? '#F0F0F0' :
                            order.status === 'Accepted' ? '#E6F2FF' :
                            order.status === 'delayed' ? '#FFF9E6' :
                            order.status === 'Delivered' ? '#E6F7EE' :
                            order.status === 'rejected' ? '#FFE6E6' : '#f5f5f5',
                          color: 
                            order.status === 'requested' ? '#666666' :
                            order.status === 'Accepted' ? '#2196F3' :
                            order.status === 'delayed' ? '#FFA500' :
                            order.status === 'Delivered' ? '#4CAF50' :
                            order.status === 'rejected' ? '#F44336' : '#666',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontWeight: 500,
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: '0 0 5px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        <Box component="span" sx={{ fontSize: '0.7rem', ml: 0.5 }}>▼</Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        bgcolor: 
                          order.status === 'requested' ? '#F0F0F0' :
                          order.status === 'Accepted' ? '#E6F2FF' :
                          order.status === 'delayed' ? '#FFF9E6' :
                          order.status === 'delivered' ? '#E6F7EE' :
                          order.status === 'rejected' ? '#FFE6E6' : '#f5f5f5',
                        color: 
                          order.status === 'requested' ? '#666666' :
                          order.status === 'Accepted' ? '#2196F3' :
                          order.status === 'delayed' ? '#FFA500' :
                          order.status === 'delivered' ? '#4CAF50' :
                          order.status === 'rejected' ? '#F44336' : '#666',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        display: 'inline-block',
                        fontWeight: 500
                      }}
                    >
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex' }}>
                    {/* Eye icon for viewing order details */}
                    <Tooltip title="View Order">
                      <IconButton 
                        size="small" 
                        sx={{ color: '#2196f3', ml:2}}
                        onClick={() => handleViewOrder(order.id)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {/* <Tooltip title="Download">
                      <IconButton size="small" sx={{ color: '#2196f3' }}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip> */}
                    {isSuperAdmin() && (
  <Tooltip title="Delete Order">
    <IconButton 
      size="small" 
      sx={{ color: '#f44336', ml: 1 }}
      onClick={() => setDeleteDialog({ open: true, orderId: order.id })}
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
  </Tooltip>
)}

                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Status Change Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem disabled sx={{ opacity: 0.7, fontSize: '0.8rem', py: 0.5 }}>
          CHANGE STATUS TO
        </MenuItem>
        <MenuItem 
          onClick={() => {
            const order = orders.find(o => o.id === menuOrderId);
            handleStatusUpdate(order, 'Accepted');
          }}
          sx={{ 
            color: '#2196F3',
            '&:hover': { bgcolor: '#E6F2FF' }
          }}
        >
          Accepted
        </MenuItem>
        <MenuItem 
          onClick={() => {
            const order = orders.find(o => o.id === menuOrderId);
            handleStatusUpdate(order, 'rejected');
          }}
          sx={{ 
            color: '#F44336',
            '&:hover': { bgcolor: '#FFE6E6' }
          }}
        >
          Rejected
        </MenuItem>
        <MenuItem 
          onClick={() => {
            const order = orders.find(o => o.id === menuOrderId);
            handleStatusUpdate(order, 'Delivered');
          }}
          sx={{ 
            color: '#4CAF50',
            '&:hover': { bgcolor: '#E6F7EE' }
          }}
        >
          Delivered
        </MenuItem>
        <MenuItem 
          onClick={() => {
            const order = orders.find(o => o.id === menuOrderId);
            handleStatusUpdate(order, 'delayed');
          }}
          sx={{ 
            color: '#FFA500',
            '&:hover': { bgcolor: '#FFF9E6' }
          }}
        >
          Delayed
        </MenuItem>
      </Menu>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, orderId: null, status: '', message: '' })}
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, orderId: null, status: '', message: '' })}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            color="primary"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
  open={deleteDialog.open}
  onClose={() => setDeleteDialog({ open: false, orderId: null })}
>
  <DialogTitle>Confirm Deletion</DialogTitle>
  <DialogContent>
    <DialogContentText>
      Are you sure you want to permanently delete this order?
    </DialogContentText>
  </DialogContent>
  <DialogActions>
    <Button 
      onClick={() => setDeleteDialog({ open: false, orderId: null })}
      color="inherit"
    >
      Cancel
    </Button>
    <Button 
      onClick={async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(CATALOG_URLS.deleteOrder(deleteDialog.orderId), {
            headers: { Authorization: `Bearer ${token}` }
          });
          setDeleteDialog({ open: false, orderId: null });

          // Refetch orders
          if (tabValue === 3) {
            fetchOrders(currentUser); // fetch admin orders
          } else {
            fetchOrders(currentUser); // fetch normal orders
          }
        } catch (error) {
          console.error('Error deleting order:', error);
          setError('Failed to delete order');
        }
      }}
      color="error"
      variant="contained"
    >
      Delete
    </Button>
  </DialogActions>
</Dialog>

    </Box>
  );
};

export default OrderPage;